import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { useT } from "@/lib/useT";

const CounterStat = ({ target, suffix = "", prefix = "", label, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.6 });

  // Stagger start
  useEffect(() => {
    if (!isInView) return;
    const timer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(timer);
  }, [isInView, delay]);

  // Count up smoothly in increasing order
  useEffect(() => {
    if (!started) return;

    const duration = 1800;
    const fps = 40;
    const totalFrames = Math.round((duration / 1000) * fps);
    let frame = 0;

    const interval = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      // Ease-out for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = eased * target;

      if (frame >= totalFrames) {
        setCount(target);
        clearInterval(interval);
      } else {
        // For decimals like 0.3, keep one decimal; for integers, round up
        if (target < 1) {
          setCount(Math.round(value * 10) / 10);
        } else {
          setCount(Math.round(value));
        }
      }
    }, 1000 / fps);

    return () => clearInterval(interval);
  }, [started, target]);

  const displayValue = target < 1
    ? count.toFixed(1)
    : count;

  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <motion.p
        className="font-heading text-3xl sm:text-4xl lg:text-7xl font-extralight text-white stat-number"
        initial={{ opacity: 0, y: 15 }}
        animate={started ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.4 }}
      >
        {prefix}{displayValue}{suffix}
      </motion.p>
      <motion.p
        className="font-body text-[9px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] text-white/40 uppercase mt-2 sm:mt-4"
        initial={{ opacity: 0 }}
        animate={started ? { opacity: 1 } : {}}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        {label}
      </motion.p>
    </div>
  );
};

const TrustStrip = () => {
  const { lang } = useT();
  const isDE = lang === "de";
  return (
    <section
      className="bg-ire-surface border-y border-white/5 py-12 sm:py-16 lg:py-24"
      data-testid="trust-strip"
    >
      <div className="max-w-[1800px] mx-auto px-6 sm:px-8 md:px-16 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-8 lg:gap-16"
        >
          <CounterStat target={100} suffix="+" label="Households Advised" delay={0} />
          <CounterStat target={24} prefix="€" suffix="K" label="Avg. Annual Benefit" delay={300} />
          <CounterStat target={200} suffix="+" label="Bank Partners" delay={600} />
          <CounterStat target={0.3} suffix="%" label="Berlin Vacancy" delay={900} />
          <CounterStat target={20} suffix="+" label="Years Experience" delay={1200} />
        </motion.div>

        {/* Lineage line — recovers the trust signal from anonymised team without showing faces */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 sm:mt-16 lg:mt-20 flex items-center justify-center"
          data-testid="trust-lineage"
        >
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center text-center">
            <span className="hidden sm:inline-block h-px w-10 lg:w-16 bg-ire-accent/40" />
            <p className="font-body text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] text-white/50 uppercase">
              <span className="text-ire-accent">{isDE ? "Gegründet 2022 · Berlin" : "Founded 2022 · Berlin"}</span>
              <span className="mx-2 sm:mx-3 text-white/20">·</span>
              <span>{isDE ? "20+ Jahre Markterfahrung" : "20+ years of German market expertise"}</span>
            </p>
            <span className="hidden sm:inline-block h-px w-10 lg:w-16 bg-ire-accent/40" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustStrip;
